import React, { useEffect } from "react";
import { useState } from "react";
import { Popover, OverlayTrigger, Modal, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { useContextStateManagement } from "../../context/ContextApi";
import "./PhoneNum.css";

export const PhoneNum = ({ isDialog = false, setIsPhoneTab, setIsTokenExpired }) => {
  const {
    usersPostRequest,
    fetchUserRecord,
    token,
    PracticeId,
    setCredentialsSaved,
    practiceName,
  } = useContextStateManagement();

  const MASKED_AUTH_TOKEN = "************************";

  const [sid, setSid] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [savedSid, setSavedSid] = useState("");
  const [savedAuthToken, setSavedAuthToken] = useState("");
  const [isCredentialsSaved, setIsCredentialsSaved] = useState(false);
  const [transferNumber, setTransferNumber] = useState("");
  const [recordingEnabled, setRecordingEnabled] = useState(true);
  const [savedRecordingEnabled, setSavedRecordingEnabled] = useState(true);
  const [sidMessage, setSidMessage] = useState("");
  const [tokenMessage, setTokenMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [assignedNumbers, setAssignedNumbers] = useState([]);
  const [fetchingNumbers, setFetchingNumbers] = useState(false);
  const [fetchingAssignedNumbers, setFetchingAssignedNumbers] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [showAssignedDetailModal, setShowAssignedDetailModal] = useState(false);
  const [selectedAssignedItem, setSelectedAssignedItem] = useState(null);

  useEffect(() => {
    const fetchCredentials = async () => {
      if (PracticeId && token) {
        setLoading(true);
        const data = await fetchUserRecord(token, `/api/getPracticeCredentials/${PracticeId}`);
        if (data === "Token is expired, login again") {
          setIsTokenExpired(true);
        } else if (data && typeof data === 'object') {
          const fetchedSid = data.accountSID || "";
          const hasAuthToken = Boolean(data.hasAuthToken);
          const maskedToken = hasAuthToken ? MASKED_AUTH_TOKEN : "";
          setSid(fetchedSid);
          setAuthToken(maskedToken);
          setSavedSid(fetchedSid);
          setSavedAuthToken(maskedToken);
          setIsCredentialsSaved(hasAuthToken);
          setTransferNumber(data.TransferNumber || "");
          const fetchedRecordingEnabled = data.recordingEnabled !== false;
          setRecordingEnabled(fetchedRecordingEnabled);
          setSavedRecordingEnabled(fetchedRecordingEnabled);
        }
        setLoading(false);
      }
    };
    fetchCredentials();
  }, [PracticeId, token]);

  const fetchAvailableNumbers = async () => {
    if (PracticeId && token) {
      setFetchingNumbers(true);
      const data = await fetchUserRecord(token, `/api/getPurchasedPhoneNumbers?practiceId=${PracticeId}`);
      if (data && Array.isArray(data)) {
        setAvailableNumbers(data);
      } else if (data === "Token is expired, login again") {
        setIsTokenExpired(true);
      }
      setFetchingNumbers(false);
    }
  };

  const fetchAssignedNumbers = async () => {
    if (PracticeId && token) {
      setFetchingAssignedNumbers(true);
      const data = await fetchUserRecord(token, `/api/getAssignedPhoneNumbers?practiceId=${PracticeId}`);
      if (data && Array.isArray(data)) {
        setAssignedNumbers(data);
      } else if (data === "Token is expired, login again") {
        setIsTokenExpired(true);
      }
      setFetchingAssignedNumbers(false);
    }
  };

  // Keyed on the persisted sid/token (only ever change on the initial
  // credentials fetch or a successful save), not the live inputs — those
  // change on every keystroke while typing a replacement token, which used
  // to re-fire both number lookups on every character typed.
  useEffect(() => {
    if (savedSid && savedAuthToken && PracticeId && token) {
      fetchAvailableNumbers();
      fetchAssignedNumbers();
    }
  }, [savedSid, savedAuthToken, PracticeId, token]);

  useEffect(() => {
    if (authToken !== savedAuthToken) {
      setIsCredentialsSaved(false);
    }
  }, [authToken, savedAuthToken]);

  const resetErrorFieldStyle = (e) => {
    if (e.target.classList.contains("empty")) {
      e.target.classList.remove("empty");
    }
    if (e.target.id === "accountSID") {
      setSidMessage("");
    }
    if (e.target.id === "authToken") {
      setTokenMessage("");
    }
  };

  const handleAuthTokenFocus = (e) => {
    resetErrorFieldStyle(e);
    if (isCredentialsSaved && authToken === MASKED_AUTH_TOKEN) {
      setAuthToken("");
      setIsCredentialsSaved(false);
    }
  };

  const handleSave = async (e) => {
    let isFieldEmpty = false;
    let sidElement = document.getElementById("accountSID");
    let tokenElement = document.getElementById("authToken");

    if (sid === "") {
      sidElement.classList.add("empty");
      isFieldEmpty = true;
      setSidMessage("Enter account sid");
    }
    const isPlaceholderToken = isCredentialsSaved && authToken === MASKED_AUTH_TOKEN;
    const hasNewAuthToken = authToken.trim() !== "" && !isPlaceholderToken;

    if (!isCredentialsSaved && authToken === "") {
      tokenElement.classList.add("empty");
      isFieldEmpty = true;
      setTokenMessage("Enter auth token");
    }
    if (isFieldEmpty === true) {
      return;
    }

    let url = "/api/saveTwilioCredentials";
    let obj = {
      accountSID: sid,
      transferNumber: transferNumber,
      practiceId: PracticeId,
      recordingEnabled: recordingEnabled,
    };

    if (hasNewAuthToken) {
      obj.authToken = authToken;
    }

    const userData = await usersPostRequest(token, url, obj);

    if (userData === "Token is expired, login again") {
      setIsTokenExpired(true);
    } else if (userData === "Credentials saved successfully" || (userData && userData.message === "Credentials saved successfully")) {
      alert("Credentials saved successfully");
      setSavedSid(sid);
      setSavedAuthToken(hasNewAuthToken ? MASKED_AUTH_TOKEN : savedAuthToken);
      setAuthToken(hasNewAuthToken ? MASKED_AUTH_TOKEN : authToken);
      setIsCredentialsSaved(true);
      setCredentialsSaved(true);
      setSavedRecordingEnabled(recordingEnabled);
      fetchAvailableNumbers();
      fetchAssignedNumbers();
    } else {
      alert(userData || "An error occurred while saving credentials");
    }
  };

  const hasUnsavedCredentialChanges =
    sid !== savedSid ||
    authToken !== savedAuthToken ||
    recordingEnabled !== savedRecordingEnabled;

  const handleConfigure = (number) => {
    setSelectedNumber(number);
    setShowConfigModal(true);
  };

  const executeConfiguration = async (type) => {
    if (!selectedNumber) return;

    setLoading(true);
    setShowConfigModal(false);

    let url = "";
    if (type === 'inbound') {
      url = "/api/configureInboundNumber";
    } else if (type === 'outbound') {
      url = "/api/configureOutboundNumber";
    } else {
      url = "/api/configureExistingNumber";
    }

    const obj = {
      practiceId: PracticeId,
      phoneNumberSid: selectedNumber.sid,
    };

    const response = await usersPostRequest(token, url, obj);
    if (response === "Token is expired, login again") {
      setIsTokenExpired(true);
    } else if (response && (response.success || (response.message && response.message.includes("configured successfully")))) {
      alert(`${type.charAt(0).toUpperCase() + type.slice(1)} configuration successful`);
      setCredentialsSaved(true);
      fetchAssignedNumbers();
      setIsPhoneTab(false);
    } else {
      alert(response?.error || response?.message || "Failed to configure number");
    }
    setLoading(false);
    setSelectedNumber(null);
  };

  // Group assigned numbers so each phone number appears only once in the table
  const groupedAssignedNumbers = Object.values(
    assignedNumbers.reduce((acc, item) => {
      const key = item.phoneNumber;
      if (!acc[key]) {
        acc[key] = {
          phoneNumber: item.phoneNumber,
          friendlyName: item.friendlyName || item.phoneNumber,
          inbound: false,
          outbound: false,
          agentName: null,
          hasLiveKitTrunks: false,
          sid: item.sid || null,
        };
      }

      if (item.assignmentType === "Practice Inbound") {
        acc[key].inbound = true;
        if (!acc[key].agentName && practiceName) {
          acc[key].agentName = practiceName;
        }
      } else if (item.assignmentType === "Practice Outbound") {
        acc[key].outbound = true;
        if (!acc[key].agentName && practiceName) {
          acc[key].agentName = practiceName;
        }
      } else if (item.assignmentType === "Agent") {
        acc[key].agentName = item.providerName || practiceName || "—";
      }
      if (item.hasLiveKitTrunks) acc[key].hasLiveKitTrunks = true;
      if (item.sid) acc[key].sid = item.sid;

      return acc;
    }, {})
  );

  const openAssignedDetail = (item) => {
    setSelectedAssignedItem(item);
    setShowAssignedDetailModal(true);
  };

  const handleUnassign = async () => {
    if (!selectedAssignedItem?.sid) return;
    setLoading(true);
    const response = await usersPostRequest(token, "/api/unassignPhoneNumber", {
      practiceId: PracticeId,
      phoneNumberSid: selectedAssignedItem.sid,
    });
    if (response === "Token is expired, login again") {
      setIsTokenExpired(true);
    } else if (response && response.success) {
      setShowAssignedDetailModal(false);
      setSelectedAssignedItem(null);
      fetchAssignedNumbers();
      fetchAvailableNumbers();
      alert("Number unassigned successfully.");
    } else {
      alert(response?.error || response?.message || "Failed to unassign number");
    }
    setLoading(false);
  };

  const handleDeletePhoneData = async () => {
    if (!selectedAssignedItem?.phoneNumber) return;
    setLoading(true);
    const direction = selectedAssignedItem.inbound ? "inbound" : "outbound";
    const response = await usersPostRequest(token, "/api/deletePhoneNumberData", {
      practiceId: PracticeId,
      phoneNumber: selectedAssignedItem.phoneNumber,
      direction,
    });
    if (response === "Token is expired, login again") {
      setIsTokenExpired(true);
    } else if (response && response.message === "Deleted") {
      setShowAssignedDetailModal(false);
      setSelectedAssignedItem(null);
      fetchAssignedNumbers();
      fetchAvailableNumbers();
      alert("Phone data deleted successfully.");
    } else {
      alert(response?.error || response?.message || "Failed to delete phone data");
    }
    setLoading(false);
  };

  const shellClassName = isDialog ? "phone-settings-shell phone-settings-shell--dialog" : "container";
  const cardWrapClassName = isDialog
    ? "phone-settings-card-wrap phone-settings-card-wrap--dialog"
    : "col-12 col-sm-12 col-md-6 col-lg-8 col-xl-8 mx-auto mt-5";
  const surfaceClassName = isDialog
    ? "phone-settings-surface phone-settings-surface--dialog"
    : "phone-settings-surface";

  return (
    <div className={shellClassName}>
      <div className={cardWrapClassName}>
        <div className={surfaceClassName}>
          <div className="phone-settings-body">
            <h3 className="phone-settings-title">
              Credentials
            </h3>
            {loading ? <p className="phone-settings-loading">Loading credentials...</p> : (
              <>
                {/* ACCOUNT SID */}
                <div className="phone-settings-field">
                  <label className="phone-settings-field-label">
                    Account SID
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <Popover id="sid-popover" className="phone-settings-popover">
                          <Popover.Body>
                            The unique identifier for your Twilio account.
                          </Popover.Body>
                        </Popover>
                      }
                    >
                      <FontAwesomeIcon
                        icon={faCircleInfo}
                        className="phone-settings-info-icon"
                      />
                    </OverlayTrigger>
                  </label>

                  <div className="phone-settings-field-input">
                    <input
                      type="text"
                      id="accountSID"
                      name="username"
                      autoComplete="username"
                      className="phone-settings-input"
                      placeholder="Enter Account SID"
                      value={sid}
                      onChange={(e) => setSid(e.target.value)}
                      onFocus={resetErrorFieldStyle}
                    />

                    {sidMessage && (
                      <small className="phone-settings-field-error">{sidMessage}</small>
                    )}
                  </div>
                </div>

                {/* AUTH TOKEN */}
                <div className="phone-settings-field">
                  <label className="phone-settings-field-label">
                    Auth Token
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <Popover id="token-popover" className="phone-settings-popover">
                          <Popover.Body>
                            Token to authenticate API requests for the given account
                            SID.
                          </Popover.Body>
                        </Popover>
                      }
                    >
                      <FontAwesomeIcon
                        icon={faCircleInfo}
                        className="phone-settings-info-icon"
                      />
                    </OverlayTrigger>
                  </label>

                  <div className="phone-settings-field-input">
                    <input
                      type={isCredentialsSaved ? "password" : "text"}
                      id="authToken"
                      name="password"
                      autoComplete="new-password"
                      className="phone-settings-input"
                      placeholder="Enter Auth Token"
                      value={authToken}
                      onChange={(e) => setAuthToken(e.target.value)}
                      onFocus={handleAuthTokenFocus}
                    />

                    {tokenMessage && (
                      <small className="phone-settings-field-error">
                        {tokenMessage}
                      </small>
                    )}
                  </div>
                </div>

                {/* TRANSFER NUMBER */}
                <div className="phone-settings-field">
                  <label className="phone-settings-field-label">
                    Transfer Number
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <Popover id="transfer-popover" className="phone-settings-popover">
                          <Popover.Body>
                            Human office line to transfer calls to if AI is disabled or fails.
                          </Popover.Body>
                        </Popover>
                      }
                    >
                      <FontAwesomeIcon
                        icon={faCircleInfo}
                        className="phone-settings-info-icon"
                      />
                    </OverlayTrigger>
                  </label>

                  <div className="phone-settings-field-input">
                    <input
                      type="text"
                      id="transferNumber"
                      className="phone-settings-input"
                      placeholder="e.g. +1234567890"
                      value={transferNumber}
                      onChange={(e) => setTransferNumber(e.target.value)}
                    />
                  </div>
                </div>

                {/* CALL RECORDING */}
                <div className="phone-settings-field phone-settings-field--switch">
                  <label
                    className="phone-settings-field-label phone-settings-field-label--switch"
                    htmlFor="recordingEnabled"
                  >
                    Call Recording
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <Popover id="recording-popover" className="phone-settings-popover">
                          <Popover.Body>
                            Records the AI conversation segment of every call
                            (not human-transferred segments). Turn this off if
                            this account doesn't need call recordings.
                          </Popover.Body>
                        </Popover>
                      }
                    >
                      <FontAwesomeIcon
                        icon={faCircleInfo}
                        className="phone-settings-info-icon"
                      />
                    </OverlayTrigger>
                  </label>

                  <div className="phone-settings-switch form-check form-switch">
                    <input
                      type="checkbox"
                      role="switch"
                      id="recordingEnabled"
                      className="form-check-input"
                      checked={recordingEnabled}
                      onChange={(e) => setRecordingEnabled(e.target.checked)}
                    />
                    <label
                      className="phone-settings-switch-label"
                      htmlFor="recordingEnabled"
                    >
                      {recordingEnabled ? "Enabled" : "Disabled"}
                    </label>
                  </div>
                </div>
              </>
            )}

            {/* ASSIGNED NUMBERS (from these credentials) */}
            {/* Gated on the persisted sid/token, not the live inputs above —
                clicking into the Auth Token field to replace it clears the
                live `authToken` value, which used to unmount this section
                even though the already-fetched data was still in state. */}
            {(savedSid && savedAuthToken) && (
              <div className="phone-settings-section">
                <h4 className="phone-settings-section-title">Numbers Assigned to Agents</h4>
                {fetchingAssignedNumbers ? (
                  <p className="phone-settings-loading">Loading assigned numbers...</p>
                ) : assignedNumbers.length > 0 ? (
                  <div className="phone-settings-table-wrap phone-settings-table-wrap--assigned">
                    <table className="phone-settings-table">
                      <thead>
                        <tr>
                          <th>Phone Number</th>
                          <th>Friendly Name</th>
                          <th>Assignment</th>
                          <th>Agent / Role</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupedAssignedNumbers.map((item) => {
                          let assignment = "—";
                          if (item.inbound && item.outbound) {
                            assignment = "Inbound & Outbound";
                          } else if (item.inbound) {
                            assignment = "Inbound";
                          } else if (item.outbound) {
                            assignment = "Outbound";
                          }

                          return (
                            <tr key={item.phoneNumber}>
                              <td>{item.phoneNumber}</td>
                              <td>{item.friendlyName}</td>
                              <td>{assignment}</td>
                              <td>{item.agentName || practiceName || "—"}</td>
                              <td>
                                <button
                                  type="button"
                                  className="phone-settings-table-btn"
                                  onClick={() => openAssignedDetail(item)}
                                  title="View details"
                                >
                                  Details
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="phone-settings-empty">No numbers are assigned yet. Configure a number below to assign it to this practice or an agent.</p>
                )}
              </div>
            )}

            {/* AVAILABLE NUMBERS */}
            {(savedSid && savedAuthToken) && (
              <div className="phone-settings-section">
                <h4 className="phone-settings-section-title">Available Numbers</h4>
                {fetchingNumbers ? (
                  <p className="phone-settings-loading">Fetching available numbers...</p>
                ) : availableNumbers.filter(n => !n.trunkSid && !n.ownedByOtherPractice && (!n.voiceUrl || n.voiceUrl === "https://demo.twilio.com/welcome/voice/")).length > 0 ? (
                  <div className="phone-settings-table-wrap phone-settings-table-wrap--available">
                    <table className="phone-settings-table">
                      <thead>
                        <tr>
                          <th>Phone Number</th>
                          <th>Friendly Name</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {availableNumbers
                          .filter(number => !number.trunkSid && !number.ownedByOtherPractice && (!number.voiceUrl || number.voiceUrl === "https://demo.twilio.com/welcome/voice/"))
                          .map((number) => (
                            <tr key={number.sid}>
                              <td>{number.phoneNumber}</td>
                              <td>{number.friendlyName}</td>
                              <td>
                                <button
                                  className="phone-settings-table-btn phone-settings-table-btn--primary"
                                  onClick={() => handleConfigure(number)}
                                  disabled={loading}
                                >
                                  Configure
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="phone-settings-empty">No eligible numbers found for this account.</p>
                )}
              </div>
            )}

            <div className="phone-settings-actions">
              {!isDialog ? (
                <button
                  className="phone-settings-btn phone-settings-btn--ghost"
                  onClick={() => setIsPhoneTab(false)}
                >
                  Back
                </button>
              ) : null}
              <button className="phone-settings-btn phone-settings-btn--primary" onClick={handleSave} disabled={loading}>
                Save Credentials
                {hasUnsavedCredentialChanges && (
                  <span
                    className="phone-settings-unsaved-flag"
                    title="You have unsaved SID/Auth Token changes"
                  >
                    (!)
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={showConfigModal}
        onHide={() => setShowConfigModal(false)}
        centered
        className="phone-settings-config-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Configure Number</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="phone-settings-config-modal-lead">How would you like to configure <strong>{selectedNumber?.phoneNumber}</strong>?</p>
          <div className="d-grid gap-3">
            <Button
              variant="outline-success"
              className="phone-settings-config-modal-option"
              onClick={() => executeConfiguration('outbound')}
            >
              <span className="phone-settings-config-modal-option-title">Outbound Only</span>
              <small className="phone-settings-config-modal-option-hint">Configure for making calls</small>
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfigModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showAssignedDetailModal}
        onHide={() => { setShowAssignedDetailModal(false); setSelectedAssignedItem(null); }}
        centered
        className="phone-settings-config-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Assigned Number</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAssignedItem && (
            <>
              <p className="mb-2">
                <strong>{selectedAssignedItem.phoneNumber}</strong>
                {selectedAssignedItem.friendlyName && selectedAssignedItem.friendlyName !== selectedAssignedItem.phoneNumber && (
                  <span className="phone-settings-config-modal-muted ms-2">({selectedAssignedItem.friendlyName})</span>
                )}
              </p>
              <p className="mb-2">
                Assigned to:{" "}
                {selectedAssignedItem.inbound && selectedAssignedItem.outbound
                  ? "Inbound & Outbound"
                  : selectedAssignedItem.inbound
                    ? "Inbound"
                    : selectedAssignedItem.outbound
                      ? "Outbound"
                      : "—"}
              </p>
              <p className="mb-0 phone-settings-config-modal-muted small">
                Agent / Role: {selectedAssignedItem.agentName || practiceName || "—"}
              </p>
              {selectedAssignedItem.hasLiveKitTrunks && (
                <div className="mt-4">
                  <div className="d-flex flex-column flex-sm-row gap-2 mt-2">
                    <Button
                      variant="outline-warning"
                      onClick={handleUnassign}
                      disabled={loading}
                    >
                      Unassign from Inbound/Outbound
                    </Button>
                    <Button
                      variant="outline-danger"
                      onClick={handleDeletePhoneData}
                      disabled={loading}
                    >
                      Delete Data
                    </Button>
                  </div>
                  <p className="phone-settings-config-modal-muted small mt-2 mb-0">
                    Unassign removes routing; Delete Data also purges related leads, transcripts, and onboarding records for this number.
                  </p>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowAssignedDetailModal(false); setSelectedAssignedItem(null); }}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

